import { IContextualMenuItem, MessageBar, MessageBarType } from '@fluentui/react';
import { BasicList, useToast, WidgetItem, WidgetPanel } from '@h2oai/ui-kit';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { hasOp } from '../../../aiem/engine/conditions';
import { FilterCondition, useEngine } from '../../../aiem/engine/hooks';
import { AIEMOpType, AIEngine } from '../../../aiem/engine/types';
import { isTransitionalState } from '../../../aiem/engine/utils';
import { Engine } from '../../../aiem/gen/ai/h2o/engine/v1/engine_pb';
import { AIEMUserActionType } from '../../../aiem/userActions';
import { usePermissions } from '../../../authz/providers/PermissionsProvider/PermissionsProvider';
import { useGetEngines } from '../../../components/AIEnginesPage/hooks/useGetEngines';
import { RoutePaths } from '../../Routes';
import { EngineActionButtonWidget } from './EngineActionButtonWidget';
import { AIEngineItem, IAppInstanceListWidgetProps } from './types';

export const aiEnginesListWidgetUserActions = [
  AIEMUserActionType.DAI_ENGINE_LIST,
  AIEMUserActionType.H2O_ENGINE_LIST,
  AIEMUserActionType.NOTEBOOK_ENGINE_LIST,
];

export const AIEnginesListWidget = (props: IAppInstanceListWidgetProps) => {
  const { title, activeWorkspace } = props;
  const history = useHistory();
  const { addToast } = useToast();
  const { searchEngines, engineTypeLogo, EngineStateMap, opOnEngine } = useEngine();
  const [canListDAI, canListH2O, canListNotebook, permissionsLoading] = usePermissions(aiEnginesListWidgetUserActions);

  const canListEngines = canListDAI || canListH2O || canListNotebook;
  const interactionRestricted = !canListEngines && !permissionsLoading;

  const [loadingMessage, setLoadingMessage] = useState('');

  const fetchEngines = useCallback(
    async (filter?: FilterCondition[]) => {
      try {
        return await searchEngines(filter || []);
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    [searchEngines]
  );

  const engines = useGetEngines(fetchEngines, { refetchInterval: 5000 }, !canListEngines);

  const getAIEngineItem = useCallback(
    (engine: Engine): AIEngineItem => {
      const { displayName, state, uid, type } = engine;
      const logo = engineTypeLogo[type!];
      const stateData = EngineStateMap.get(state!);

      return {
        id: uid || '',
        title: displayName || '',
        iconLocation: logo.src,
        iconBackgroundColor: logo.backgroundColor,
        status: stateData?.title || 'Unknown',
        statusColor: stateData?.backgroundColor,
        statusTextColor: stateData?.color,
        statusLoading: isTransitionalState(state),
      };
    },
    [engineTypeLogo]
  );

  const modifyEngine = useCallback(
    async (engine: Engine, op: AIEMOpType) => {
      try {
        await opOnEngine(engine as AIEngine, op);
        addToast({
          message: `Successfully ${op === AIEMOpType.pause ? 'paused' : 'resumed'} engine with id: ${engine.uid}`,
          messageBarType: MessageBarType.success,
        });
      } catch {
        addToast({
          message: `Failed to ${op === AIEMOpType.pause ? 'pause' : 'resume'} engine with id: ${engine.uid}`,
          messageBarType: MessageBarType.error,
        });
      } finally {
        engines.refetch();
      }
    },
    [opOnEngine, addToast]
  );

  const modifyEngines = useCallback(
    (op: AIEMOpType) => {
      let completed = 0;
      const eligible = engines.data?.filter(({ state, type }) => hasOp(op, type, state)) || [];
      const total = eligible.length;

      const updateMessage = () =>
        setLoadingMessage(`${completed} of ${total} engines ${op === AIEMOpType.pause ? 'paused' : 'resumed'}`);

      setLoadingMessage(`${completed} of ${total} engines ${op === AIEMOpType.pause ? 'paused' : 'resumed'}`);

      eligible.forEach(async (engine) => {
        try {
          await opOnEngine(engine as AIEngine, op);
        } catch {
          addToast({
            message: `Failed to ${op === AIEMOpType.pause ? 'pause' : 'resume'} engine with id: ${engine.uid}`,
            messageBarType: MessageBarType.error,
          });
        } finally {
          completed += 1;
          updateMessage();
          if (completed === total) setLoadingMessage('');
        }
      });
    },
    [engines.data, addToast, opOnEngine]
  );

  const contextMenuItems: IContextualMenuItem[] = [
    { key: 'resume', text: 'Resume all engines', onClick: () => modifyEngines(AIEMOpType.resume) },
    { key: 'pause', text: 'Pause all engines', onClick: () => modifyEngines(AIEMOpType.pause) },
  ];

  const emptyMessageProps = !engines.data?.length ? { message: `You don't have any AI engines` } : undefined;

  // Use loadingMessage for bulk operations, otherwise use engines.loading for initial load
  const effectiveLoadingMessage = loadingMessage || (engines.loading ? 'Loading' : '');

  return (
    <WidgetPanel
      title={`${title}${engines.data ? ` (${engines.data.length})` : ''}`}
      description={activeWorkspace?.displayName ? `Workspace: ${activeWorkspace?.displayName}` : 'Loading workspace...'}
      linkText="View all"
      linkOnClick={() => history.push(RoutePaths.MY_AI_ENGINES)}
      loadingMessage={effectiveLoadingMessage}
      emptyMessageProps={interactionRestricted ? undefined : emptyMessageProps}
      contextMenuItems={contextMenuItems}
    >
      {canListEngines && !permissionsLoading ? (
        <BasicList
          data={engines.data?.slice(0, 5) || []}
          idField="uid"
          itemRenderer={(engine, _i, last) => {
            const item = getAIEngineItem(engine);

            return (
              <WidgetItem<AIEngineItem>
                idField="id"
                titleField="title"
                imagePathField="iconLocation"
                imageBackgroundColorField="iconBackgroundColor"
                statusField="status"
                statusColorField="statusColor"
                statusTextColorField="statusTextColor"
                statusLoadingField="statusLoading"
                last={last}
                data={item}
                imageHref={engine.loginUrl}
                titleHref={engine.loginUrl}
                actions={<EngineActionButtonWidget engine={engine} modifyEngine={modifyEngine} />}
              />
            );
          }}
        />
      ) : (
        <MessageBar styles={{ root: { marginTop: 16 } }} messageBarType={MessageBarType.error}>
          You are not authorized to access AI Engine Settings in this Workspace.
        </MessageBar>
      )}
    </WidgetPanel>
  );
};
